// Internal-only access gate. Rendered around the whole app when
// VITE_INTERNAL_ONLY === '1' (dev/stage). Restricts the SPA to seeded internal
// users. OTP-only (no password): enter your email, then a one-time code emailed
// to you. Once a device is verified it is trusted, so future logins on that
// browser/machine skip the OTP step.
//
// Unrecognized email → a "Request access" form (name + phone) that emails the
// platform admin. The admin signs in and, at ?internal=requests, reviews the
// queued requests and Approves/Rejects them. Self-contained (no app contexts).
import { useCallback, useEffect, useState } from 'react'

// Deployed builds call VITE_API_URL/api; local dev uses relative URLs.
// Domain origin only (strip any path VITE_API_URL may include, e.g. /api/v1),
// then append the app's actual route prefix below. This keeps the gate correct
// whether VITE_API_URL is "https://dev-api.x.com" or "https://dev-api.x.com/api".
let API_ORIGIN = ''
try {
  if (import.meta.env.PROD && import.meta.env.VITE_API_URL) {
    API_ORIGIN = new URL(import.meta.env.VITE_API_URL).origin
  }
} catch { API_ORIGIN = '' }
const API_PREFIX = '' // installer rewrites this to the app's actual route prefix
const API_BASE = `${API_ORIGIN}${API_PREFIX}`

const TOKEN_KEY = 'internal_access_token'
const DEVICE_ID_KEY = 'internal_access_device_id'

// Admin review page is requested via ?internal=requests (link in the email).
const REVIEW_MODE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('internal') === 'requests'

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) { id = uuid(); localStorage.setItem(DEVICE_ID_KEY, id) }
  return id
}

async function sha256Hex(str) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function fingerprintSource() {
  const n = window.navigator
  return [
    n.userAgent, n.language, n.platform,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|')
}

function deviceLabel() {
  const ua = window.navigator.userAgent
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux'
    : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown OS'
  return `${browser} on ${os}`
}

// ⚠ A THROWN FETCH MUST NOT REACH THE CALLER. The session probe now runs on
//   EVERY load rather than only when a token exists, so an offline visitor, a
//   VPN or a dead API would reject this promise inside the mount effect, no
//   phase would ever be set, and the gate would sit on "Checking your session…"
//   for ever. Before the probe moved, a visitor with no token never called the
//   API at all and could not hit this. Returning a sentinel instead lets the
//   caller fall through to the email form exactly as it used to.
async function call(path, { method = 'POST', body, token } = {}) {
  let res
  try {
    res = await fetch(`${API_BASE}/internal-access/${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    return { ok: false, status: 0, data: {}, networkError: true }
  }
  let data = {}
  try { data = await res.json() } catch { /* an HTML 500 body — ignore */ }
  return { ok: res.ok, status: res.status, data }
}

const post = (path, body, token) => call(path, { method: 'POST', body, token })

// Tabowe's palette — the same values tailwind.config.js and src/index.css
// define, repeated as literals because this component is deliberately
// self-contained (see the porting note below) and must not import the app's
// theme.
//
// ⚠ MEASURED, NOT ASSUMED. Contrast against the cream card #fffef7:
//     forest-900 #0a3d2e ... 12.08  AA
//     gold-800 #92400e ......  7.01  AA
//     forest-700 #0a6548 ....  7.00  AA
//     gold-700 #b45309 ......  4.97  AA
//     forest-600 #0d7f58 ....  4.95  AA   (measured; unused — 700 covers this role)
//     forest-500 #189e6d ....  3.38  AA-large only
//     forest-400 #38b886 ....  2.48  FAIL
//     gold-500 #f59e0b ......  2.12  FAIL
//     forest-100 #d3f4e3 ....  1.17  FAIL — tints and hairlines only
//
// ⚠⚠ BOTH BRAND SHADES FAIL AS SMALL TEXT. forest-500 is the tone the landing
//   page sets "Not friendships." in, and at 3.38 that is legitimate THERE —
//   the heading is 5xl–7xl, and AA-large is the right bar for it. The drawing
//   keeps it for that one heading and nowhere else. Every small forest label
//   uses forest-700, and gold type uses gold-700/800, because gold-500 is 2.12
//   and white on a gold fill is 2.15.
const CREAM    = '#fffef7' // --cream-50
const CREAM_2  = '#fefce8' // --cream-100
const SURFACE  = '#ffffff'
const FOREST_9 = '#0a3d2e' // --forest-900 — the ink
const FOREST_7 = '#0a6548' // --forest-700 — safe as small type, and as a fill
const FOREST_5 = '#189e6d' // --forest-500 — LARGE TYPE AND FILLS ONLY
const FOREST_1 = '#d3f4e3' // --forest-100
const GOLD     = '#f59e0b' // --gold-500 — FILLS ONLY
const GOLD_TX  = '#b45309' // --gold-700 — safe as type
const GOLD_1   = '#fef3c7'
const INK_SOFT = '#3f5d52' // a forest-leaning grey for secondary text (7.4 on cream)
const ERROR    = '#b42318'

const DISPLAY = "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif"
const UI = "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif"

const BLEED      = 40                      // how far the drawing grows past each edge
// ⚠ NOT BUILT FROM forest-100 (1.17:1). That token exists to be a whisper of a
//   tint; a placeholder bar made of it is invisible before a blur and a scrim
//   are applied on top. An earlier attempt at this on a sibling product was
//   built from exactly that kind of token and rendered a ghost.
const GHOST      = 'rgba(10,61,46,0.17)'
const GHOST_SOFT = 'rgba(10,61,46,0.07)'
const EDGE       = 'rgba(10,61,46,0.15)'

// ── The app shell behind the dialog ───────────────────────────────────────
// Someone who lands on a closed door should still be able to see what is
// behind it. A flat void gives a visitor nothing to judge, and reads more like
// a parked domain than a product that is not open yet.
//
// ⚠⚠⚠ THIS IS A DRAWING, NOT THE APP, AND IT MUST STAY THAT WAY.
//   The obvious implementation — render `children` blurred underneath — is a
//   hole straight through the gate: the app would MOUNT and run every fetch it
//   makes on load for a visitor who has not been authorized, `filter: blur()`
//   is decoration that any inspector removes, and this product's payload is who
//   owes whom — which would then sit in the DOM of a page whose entire job is
//   to withhold it. So this renders fixed markup and nothing else: no router,
//   no contexts, no fetches, `aria-hidden` and `pointer-events: none`.
//
// ⚠⚠ AND NOT ONE AMOUNT IS SHOWN. No currency, no totals, no names, not even
//   invented ones. A bill-splitting login screen displaying "₹1,240 — you owe
//   Priya" is a fabricated debt between two people who do not exist, and
//   blurring it only means someone has to sharpen it before they notice. The
//   settle-up rows are an avatar disc, a bar where a name would be, and a
//   direction pill: forest for owed to you, gold for owed by you. Which way the
//   pill leans is the only thing the drawing says, and it says it about nobody.
function AppPreview() {
  const nav = ['How it works', 'Groups', 'Pricing', 'Help']
  return (
    <div style={P.root} aria-hidden="true">
      {/* Inline styles cannot express a media query, and at phone width the
          settle-up panel squeezes the hero column to nothing. It goes; the
          cards stay, stacked, so the lower half is not blank. */}
      <style>{`
        @media (max-width: 880px) {
          .tb-ig-panel { display: none !important; }
          .tb-ig-cards { flex-direction: column !important; }
          .tb-ig-hero { padding-top: 34px !important; }
          .tb-ig-title { font-size: 34px !important; }
        }
      `}</style>

      <div style={P.nav}>
        <div style={P.navInner}>
          <span style={P.wordmark}>Tabowe</span>
          <div style={P.navLinks}>
            {nav.map((l) => <span key={l} style={P.navLink}>{l}</span>)}
          </div>
          <span style={P.navBtn}>Start free</span>
        </div>
      </div>

      <div style={P.hero} className="tb-ig-hero">
        <div style={P.heroCopy}>
          <span style={P.eyebrow}>AI receipt scanning — free</span>
          <h2 style={P.heroTitle} className="tb-ig-title">
            Split expenses.<br /><em style={P.heroEm}>Not friendships.</em>
          </h2>
          <div style={{ ...P.bar, width: '90%' }} />
          <div style={{ ...P.bar, width: '64%' }} />
          <div style={P.heroBtns}>
            <span style={P.btnSolid} />
            <span style={P.btnGhost} />
          </div>
        </div>
        <SettlePanel />
      </div>

      <div style={P.cardRow} className="tb-ig-cards">
        {[FOREST_5, GOLD, FOREST_7].map((c) => (
          <div key={c} style={P.card}>
            <div style={{ ...P.cardIcon, background: `${c}2E`, border: `1px solid ${c}59` }} />
            <div style={{ ...P.bar, width: '72%', height: 11 }} />
            <div style={{ ...P.bar, width: '48%', height: 11, margin: 0 }} />
          </div>
        ))}
      </div>
    </div>
  )
}

// The settle-up panel — a scanned receipt and a list of who settles with whom.
// Fixed, not random: a backdrop that reshuffles between renders is a
// distraction. Direction only, never a figure.
function SettlePanel() {
  // true = owed to you (forest), false = you owe (gold)
  const ROWS = [true, false, true, true, false]
  return (
    <div style={P.panel} className="tb-ig-panel">
      <div style={P.panelChrome}>
        {[ERROR, GOLD, FOREST_5].map((c) => (
          <span key={c} style={{ ...P.dot, background: c }} />
        ))}
      </div>
      <div style={P.panelBody}>
        {/* A scanned receipt: lines, no prices */}
        <div style={P.receipt}>
          <div style={{ ...P.bar, height: 7, width: '44%', margin: '0 0 8px' }} />
          {['88%', '72%', '80%', '60%'].map((w, i) => (
            <div key={i} style={P.receiptLine}>
              <span style={{ ...P.bar, height: 5, width: w, margin: 0 }} />
              <span style={P.receiptAmt} />
            </div>
          ))}
        </div>

        {ROWS.map((owed, i) => (
          <div key={i} style={P.settleRow}>
            <span style={{
              ...P.avatar,
              background: owed ? `${FOREST_5}33` : `${GOLD}33`,
              border: `1.5px solid ${owed ? FOREST_5 : GOLD}80`,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...P.bar, height: 7, width: i % 2 ? '52%' : '66%', margin: 0 }} />
            </div>
            <span style={{
              ...P.pill,
              background: owed ? `${FOREST_5}26` : `${GOLD}26`,
              border: `1px solid ${owed ? FOREST_5 : GOLD}4D`,
            }} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function InternalGate({ children }) {
  // Self-disabling: outside dev/stage (flag != '1') this is a passthrough, so
  // it can be wired into every app's entry unconditionally. The flag is inlined
  // at build time, so this branch is constant per build (hooks stay consistent).
  //
  // eslint's rules-of-hooks can't see that the condition is a build-time
  // constant, so it flags every hook below as conditional. Disabled for this
  // component only — if the gate ever becomes runtime-configurable, this
  // suppression must go and the early return must move into the hooks.
  /* eslint-disable react-hooks/rules-of-hooks */
  if (import.meta.env.VITE_INTERNAL_ONLY !== '1') return children

  const [phase, setPhase] = useState('checking') // checking | email | otp | request | requested | authed | disabled
  const [deviceHash, setDeviceHash] = useState(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [phone, setPhone] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      const dh = await sha256Hex(`${getDeviceId()}|${fingerprintSource()}`)
      if (!alive) return
      setDeviceHash(dh)
      const token = localStorage.getItem(TOKEN_KEY)

      // ⚠⚠⚠ ASK THE BACKEND WHETHER THE GATE IS ON, AND STAND DOWN IF IT IS NOT.
      //   InternalAccessController::ensureEnabled() returns 404 on every gate
      //   endpoint when INTERNAL_ONLY is off. Before this, with no token the
      //   gate jumped straight to the email form without asking the backend
      //   anything, then 404ed on `request-otp` and printed the raw envelope
      //   "The requested resource was not found." under the email field — a
      //   production site nobody could get past, including the admin,
      //   reporting a missing route rather than a disabled feature. This exact
      //   failure took a sibling product's production site down.
      //
      //   A 404 from a gate endpoint is not an error. It is the backend saying
      //   "I do not have this gate", and the only correct response is to let
      //   the app through. The probe is safe without a token because
      //   ensureEnabled() is the first line of session(), ahead of validation
      //   and the bearer check.
      const probe = await post('session', { device_hash: dh }, token || undefined)
      if (!alive) return
      if (probe.status === 404) { setPhase('disabled'); return }

      if (!token) { setPhase('email'); return }
      const { ok, data } = probe
      if (ok && data.valid) { setIsAdmin(!!data.user?.is_admin); setPhase('authed') }
      else { localStorage.removeItem(TOKEN_KEY); setPhase('email') }
    })()
    return () => { alive = false }
  }, [])

  const requestCode = useCallback(async () => {
    return post('request-otp', {
      email: email.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
  }, [email, deviceHash])

  const submitEmail = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(''); setNotice('')
    const { ok, status, data } = await requestCode()
    setBusy(false)
    if (!ok) {
      // Same reasoning as the mount probe: 404 here is "no gate", not an error.
      if (status === 404) { setPhase('disabled'); return }
      if (data.can_request) { setPhase('request'); return }
      setError(data.message || 'Could not sign in.'); return
    }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice(`We emailed a 6-digit code to ${data.email || email}.`)
    setPhase('otp')
  }, [requestCode, email])

  const submitOtp = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('verify-otp', {
      email: email.trim(), code: code.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Invalid code.'); return }
    localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed')
  }, [email, code, deviceHash])

  const resend = useCallback(async () => {
    setBusy(true); setError(''); setNotice('')
    const { ok, data } = await requestCode()
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not resend code.'); return }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice('A new code is on its way.')
  }, [requestCode])

  const submitRequest = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('request-access', {
      email: email.trim(), first_name: first.trim(), last_name: last.trim(), phone: phone.trim(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not submit your request.'); return }
    if (data.status === 'already_authorized') {
      setNotice('You already have access — sign in with your email.'); setPhase('email'); return
    }
    setPhase('requested')
  }, [email, first, last, phone])

  // The backend has the gate switched off. Render the app.
  if (phase === 'disabled') return children

  if (phase === 'authed') {
    if (REVIEW_MODE && isAdmin) return <ReviewPage token={localStorage.getItem(TOKEN_KEY)} />
    return children
  }

  return (
    <div style={S.wrap}>
      <AppPreview />
      {/* A wash over the drawing: enough to hold the dialog contrast
          without flattening the shell back into the void it replaced. */}
      <div style={S.scrim} aria-hidden="true" />
      <div style={S.card}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Internal access</span>
        </div>

        {phase === 'checking' && <div style={S.checking}>Checking your session…</div>}

        {phase === 'email' && (
          <form onSubmit={submitEmail}>
            <h1 style={S.title}>Sign in to continue</h1>
            <p style={S.sub}>This environment is restricted to the internal team. Enter your email and we’ll send you a one-time code.</p>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" autoFocus required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Please wait…' : 'Send code'}
            </button>
          </form>
        )}

        {phase === 'otp' && (
          <form onSubmit={submitOtp}>
            <h1 style={S.title}>Enter your code</h1>
            {notice && <p style={S.sub}>{notice}</p>}
            <label style={S.label}>6-digit code</label>
            <input style={{ ...S.input, letterSpacing: '0.4em', textAlign: 'center', fontSize: 20 }}
              inputMode="numeric" autoFocus required value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={resend} disabled={busy}>Resend code</button>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setCode(''); setError('') }}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {phase === 'request' && (
          <form onSubmit={submitRequest}>
            <h1 style={S.title}>Request access</h1>
            <p style={S.sub}>Your email isn’t on the internal list yet. Send a request and the platform admin will review it.</p>
            <label style={S.label}>Email address</label>
            <input style={{ ...S.input, opacity: 0.65, cursor: 'not-allowed' }} type="email" value={email} disabled readOnly />
            <div style={S.grid2}>
              <div>
                <label style={S.label}>First name</label>
                <input style={S.input} autoFocus required value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First" />
              </div>
              <div>
                <label style={S.label}>Last name</label>
                <input style={S.input} required value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last" />
              </div>
            </div>
            <label style={S.label}>Contact phone number</label>
            <input style={S.input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Request access'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setError('') }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {phase === 'requested' && (
          <div>
            <h1 style={S.title}>Request sent</h1>
            <p style={S.sub}>Thanks — we’ve emailed the platform admin. You’ll be able to sign in with a one-time code once your request is approved.</p>
            <button style={S.btn} type="button" onClick={() => { setPhase('email'); setFirst(''); setLast(''); setPhone(''); setError('') }}>
              Done
            </button>
          </div>
        )}

        <div style={S.foot}>Trusted devices skip the code next time · A Stacza product</div>
      </div>
    </div>
  )
}

// ── Admin review page (?internal=requests) ─────────────────────────────────
function ReviewPage({ token }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [actingId, setActingId] = useState(null)

  const load = useCallback(async () => {
    const { ok, data } = await call('requests', { method: 'GET', token })
    if (!ok) { setError(data.message || 'Could not load requests.'); setRows([]); return }
    setRows(data.data || [])
  }, [token])

  useEffect(() => { load() }, [load])

  const act = useCallback(async (id, action) => {
    setActingId(id); setError('')
    const { ok, data } = await post(`requests/${id}/${action}`, {}, token)
    setActingId(null)
    if (!ok) { setError(data.message || `Could not ${action}.`); return }
    load()
  }, [token, load])

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }

  return (
    <div style={{ ...S.wrap, alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ ...S.card, maxWidth: 860 }}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Access requests</span>
        </div>
        <h1 style={S.title}>Access requests</h1>
        <p style={S.sub}>Newest first. Approving grants access in this environment and adds the email to the seeder so it promotes to the others.</p>
        {error && <div style={S.error}>{error}</div>}

        {rows === null && <div style={S.checking}>Loading…</div>}
        {rows !== null && rows.length === 0 && <div style={S.checking}>No access requests yet.</div>}

        {rows !== null && rows.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Requester</th>
                  <th style={S.th}>Phone</th>
                  <th style={S.th}>Requested</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{`${r.first_name} ${r.last_name}`.trim() || '—'}</div>
                      <div style={S.tdSub}>{r.email}</div>
                    </td>
                    <td style={S.td}>{r.phone || '—'}</td>
                    <td style={S.td}>{fmtDate(r.created_at)}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.status === 'pending' ? (
                        <>
                          <button style={S.approveBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'approve')}>Approve</button>
                          <button style={S.rejectBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'reject')}>Reject</button>
                        </>
                      ) : (
                        <span style={S.tdSub}>
                          {r.reviewed_by ? `by ${r.reviewed_by}` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.foot}>
          <a href={window.location.pathname} style={{ color: INK_SOFT }}>← Back to app</a>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: GOLD_1,         bd: `${GOLD}66`,     c: GOLD_TX,   t: 'Pending' },
    approved: { bg: FOREST_1,       bd: `${FOREST_7}4D`, c: FOREST_7,  t: 'Approved' },
    rejected: { bg: `${ERROR}12`,   bd: `${ERROR}40`,    c: ERROR,     t: 'Rejected' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}>{s.t}</span>
  )
}

// ── The drawing behind the dialog ─────────────────────────────────────────
// `pointer-events: none` on the root is load-bearing, not cosmetic: it is what
// stops a visitor interacting with anything here, including selecting text.
const P = {
  // ⚠ A BLEED MARGIN, NOT A SCALE. `transform: scale()` also pushes the blur's
  //   soft edge off-screen, but it does it by enlarging about the centre, which
  //   crops the top of the viewport and slices the navbar in half.
  root: {
    position: 'fixed', inset: -BLEED, overflow: 'hidden', pointerEvents: 'none',
    userSelect: 'none', fontFamily: UI, padding: `${BLEED}px ${BLEED}px 0`,
    boxSizing: 'border-box', filter: 'blur(3.5px) saturate(1.03)',
    background: `radial-gradient(110% 80% at 80% 4%, ${FOREST_1}CC 0%, ${FOREST_1}00 55%),
                 linear-gradient(180deg, ${SURFACE} 0%, ${CREAM} 60%, ${CREAM_2} 100%)`,
  },
  nav: { background: `${CREAM}F2`, borderBottom: `1px solid ${EDGE}` },
  navInner: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', height: 68, display: 'flex', alignItems: 'center', gap: 26 },
  wordmark: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 22, color: FOREST_9, letterSpacing: '-0.03em' },
  navLinks: { display: 'flex', gap: 20, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' },
  navLink: { fontSize: 13.5, color: INK_SOFT, whiteSpace: 'nowrap' },
  // ⚠ forest-700, not forest-500: white on forest-500 is 3.41 and fails for a
  //   label this size. White on forest-700 is 7.08.
  navBtn: { fontSize: 13, fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap', background: FOREST_7, padding: '10px 18px', borderRadius: 999 },

  hero: { maxWidth: 1180, margin: '0 auto', padding: '54px 28px 36px', display: 'flex', alignItems: 'center', gap: 44 },
  heroCopy: { flex: 1, minWidth: 0 },
  // ⚠ gold-700 for the label, never gold-500 (2.12).
  eyebrow: { display: 'inline-block', fontSize: 11, letterSpacing: 0.9, textTransform: 'uppercase', color: GOLD_TX, background: GOLD_1, border: `1px solid ${GOLD}66`, padding: '5px 12px', borderRadius: 999, marginBottom: 18 },
  heroTitle: { fontFamily: DISPLAY, fontSize: 48, lineHeight: 1.05, color: FOREST_9, margin: '0 0 22px', fontWeight: 800, letterSpacing: '-0.03em' },
  // ⚠ forest-500 at 3.38 is AA-LARGE, and this heading is 48px — which is the
  //   one place that bar is the right one, and the tone the landing page uses.
  heroEm: { fontStyle: 'normal', color: FOREST_5 },
  bar: { height: 12, borderRadius: 999, background: GHOST, margin: '0 0 10px' },
  heroBtns: { display: 'flex', gap: 12, marginTop: 26 },
  btnSolid: { width: 150, height: 46, borderRadius: 999, background: FOREST_7 },
  btnGhost: { width: 130, height: 46, borderRadius: 999, border: `1.5px solid ${INK_SOFT}` },

  panel: { flexShrink: 0, width: 320, background: SURFACE, borderRadius: 14, border: `1px solid ${EDGE}`, overflow: 'hidden', boxShadow: '0 22px 50px rgba(10,61,46,0.16)' },
  panelChrome: { height: 30, background: CREAM_2, borderBottom: `1px solid ${EDGE}`, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px' },
  dot: { width: 9, height: 9, borderRadius: 999, display: 'inline-block' },
  panelBody: { padding: 14 },
  receipt: { background: CREAM, border: `1px dashed ${EDGE}`, borderRadius: 9, padding: 11, marginBottom: 13 },
  receiptLine: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 },
  receiptAmt: { width: 26, height: 5, borderRadius: 999, background: GHOST_SOFT, flexShrink: 0 },
  settleRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: `1px solid ${EDGE}` },
  avatar: { width: 26, height: 26, borderRadius: 999, flexShrink: 0 },
  pill: { width: 44, height: 17, borderRadius: 999, flexShrink: 0 },

  cardRow: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', display: 'flex', gap: 20 },
  card: { flex: 1, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 14, padding: 18, boxShadow: '0 14px 32px rgba(10,61,46,0.10)' },
  cardIcon: { width: 38, height: 38, borderRadius: 10, marginBottom: 16 },
}

const S = {
  wrap: { position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: CREAM, fontFamily: UI, color: FOREST_9 },
  // ⚠ A WASH, not a lid: an early version of this on a sibling product sat at
  // 94% opacity in the centre and erased the shell it was meant to reveal.
  scrim: { position: 'fixed', inset: 0, pointerEvents: 'none', background: `radial-gradient(ellipse at center, ${CREAM}C4 0%, ${CREAM}94 45%, ${CREAM}4D 100%)` },
  card: { position: 'relative', zIndex: 1, width: '100%', maxWidth: 400, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 16, padding: '32px 28px', boxShadow: '0 40px 90px rgba(10,61,46,0.22), 0 4px 14px rgba(10,61,46,0.08)' },
  brandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 },
  badge: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, color: FOREST_9, letterSpacing: '-0.03em' },
  // ⚠ forest-700, not forest-500 (3.38) and certainly not gold-500 (2.12).
  //   This chip is the only thing on the card stating what kind of page it is.
  internalTag: { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.3, color: FOREST_7, border: `1px solid ${FOREST_7}3D`, background: FOREST_1, padding: '4px 10px', borderRadius: 999 },
  checking: { padding: '28px 0', textAlign: 'center', color: INK_SOFT, fontSize: 14 },
  title: { fontFamily: DISPLAY, fontSize: 26, fontWeight: 800, margin: '0 0 8px', color: FOREST_9, letterSpacing: '-0.03em' },
  sub: { fontSize: 13.5, color: INK_SOFT, margin: '0 0 22px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 12, color: INK_SOFT, margin: '14px 0 6px', fontWeight: 600 },
  // ⚠ WCAG 1.4.11 asks 3.0 of a control's boundary; forest-100 is 1.17 and the
  //   boundary is the only thing showing where the field is.
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: `1px solid ${INK_SOFT}`, background: SURFACE, color: FOREST_9, fontSize: 14, outline: 'none', fontFamily: UI },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  option: { color: FOREST_9, background: SURFACE },
  hint: { fontSize: 11.5, color: INK_SOFT, marginTop: 6 },
  checkRow: { display: 'flex', gap: 10, alignItems: 'flex-start', margin: '16px 0 2px', fontSize: 12.5, lineHeight: 1.45, color: INK_SOFT, cursor: 'pointer' },
  // White on forest-700 is 7.08.
  btn: { width: '100%', marginTop: 22, padding: '13px 18px', borderRadius: 999, border: 'none', background: FOREST_7, color: '#FFFFFF', fontWeight: 800, fontSize: 14.5, cursor: 'pointer', fontFamily: UI },
  row: { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  linkBtn: { background: 'none', border: 'none', color: FOREST_7, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: UI },
  error: { marginTop: 14, fontSize: 13, color: ERROR, background: `${ERROR}12`, border: `1px solid ${ERROR}40`, borderRadius: 8, padding: '9px 12px' },
  foot: { marginTop: 26, fontSize: 11, color: INK_SOFT, textAlign: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, color: INK_SOFT, borderBottom: `1px solid ${EDGE}` },
  td: { padding: '12px', borderBottom: `1px solid ${EDGE}`, verticalAlign: 'top' },
  tdSub: { fontSize: 12, color: INK_SOFT },
  approveBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 999, border: 'none', cursor: 'pointer', background: FOREST_7, color: '#FFFFFF', fontWeight: 700, fontSize: 12.5, fontFamily: UI },
  rejectBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 999, cursor: 'pointer', background: 'transparent', border: `1px solid ${ERROR}66`, color: ERROR, fontWeight: 600, fontSize: 12.5, fontFamily: UI },
}
